import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { Plus, Edit, Trash2, Star, FileText, X, CheckSquare, Thermometer, Droplets, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

function ChecklistList({ items, onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-2 max-h-40 overflow-y-auto">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
          <span className="text-sm">{item}</span>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(idx)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function ParameterList({ items, onRemove }) {
  if (!items || items.length === 0) return null;
  
  const formatParam = (p) => p.unit ? p.name + ' (' + p.unit + ')' : p.name;
  
  return (
    <div className="space-y-2 max-h-40 overflow-y-auto">
      {items.map((param, idx) => (
        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
          <span className="text-sm">{formatParam(param)}</span>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(idx)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function TemplateCard({ template, onEdit, onDelete, onSetDefault }) {
  const checklistCount = template.checklist_items ? template.checklist_items.length : 0;
  const paramsCount = template.parameters ? template.parameters.length : 0;
  const bgColor = template.primary_color || '#0284c7';

  // Generate gradient based on primary color
  const gradientStyle = {
    background: `linear-gradient(135deg, ${bgColor}15 0%, ${bgColor}05 100%)`
  };

  return (
    <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${template.is_default ? 'ring-2 ring-sky-500 shadow-sky-100' : 'hover:ring-1 hover:ring-slate-200'}`}>
      {/* Decorative top bar with template color */}
      <div className="h-1.5 w-full" style={{ backgroundColor: bgColor }} />
      
      <CardHeader className="pb-3" style={gradientStyle}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-2">
            <CardTitle className="text-lg font-semibold truncate flex items-center gap-2">
              <FileText className="w-5 h-5 flex-shrink-0" style={{ color: bgColor }} />
              {template.name}
            </CardTitle>
            {template.is_default && (
              <Badge className="mt-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-0">
                <Star className="w-3 h-3 mr-1 fill-current" />
                Predeterminada
              </Badge>
            )}
          </div>
          <div 
            className="w-10 h-10 rounded-xl shadow-inner flex items-center justify-center" 
            style={{ backgroundColor: bgColor }}
          >
            <Droplets className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4 pt-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
            <CheckSquare className="w-4 h-4 text-emerald-600" />
            <div>
              <p className="text-lg font-bold text-slate-900">{checklistCount}</p>
              <p className="text-xs text-slate-500">Tareas</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
            <Thermometer className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-lg font-bold text-slate-900">{paramsCount}</p>
              <p className="text-xs text-slate-500">Parámetros</p>
            </div>
          </div>
        </div>
        
        {/* Preview of checklist items */}
        {checklistCount > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Vista previa</p>
            <div className="space-y-1">
              {template.checklist_items.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-3 h-3 rounded border border-slate-300 flex-shrink-0" />
                  <span className="truncate">{item}</span>
                </div>
              ))}
              {checklistCount > 3 && (
                <p className="text-xs text-slate-400 pl-5">+{checklistCount - 3} más...</p>
              )}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={() => onEdit(template)} className="flex-1">
            <Edit className="w-3.5 h-3.5 mr-1.5" />
            Editar
          </Button>
          {!template.is_default && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onSetDefault(template.id)}
              className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              <Star className="w-3.5 h-3.5 mr-1.5" />
              Predeterminar
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
                <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(template.id)} className="bg-red-600 hover:bg-red-700">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateList({ templates, onEdit, onDelete, onSetDefault }) {
  if (!templates || templates.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Aún no tienes plantillas personalizadas</p>
          <p className="text-sm text-slate-400 mt-1">Crea tu primera plantilla para los partes de trabajo</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {templates.map((t) => (
        <TemplateCard key={t.id} template={t} onEdit={onEdit} onDelete={onDelete} onSetDefault={onSetDefault} />
      ))}
    </div>
  );
}

const Templates = () => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newParamName, setNewParamName] = useState('');
  const [newParamUnit, setNewParamUnit] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    checklist_items: [],
    parameters: [],
    is_default: false,
    header_text: '',
    footer_text: '',
    show_photos: true,
    show_parameters: true,
    show_materials: true,
    primary_color: '#0284c7'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/report-templates');
      setTemplates(res.data);
    } catch (err) {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      checklist_items: [],
      parameters: [],
      is_default: false,
      header_text: '',
      footer_text: '',
      show_photos: true,
      show_parameters: true,
      show_materials: true,
      primary_color: '#0284c7'
    });
    setNewChecklistItem('');
    setNewParamName('');
    setNewParamUnit('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingTemplate) {
        await api.put('/report-templates/' + editingTemplate.id, formData);
        toast.success('Plantilla actualizada');
      } else {
        await api.post('/report-templates', formData);
        toast.success('Plantilla creada');
      }
      setDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      checklist_items: template.checklist_items || [],
      parameters: template.parameters || [],
      is_default: template.is_default || false,
      header_text: template.header_text || '',
      footer_text: template.footer_text || '',
      show_photos: template.show_photos !== false,
      show_parameters: template.show_parameters !== false,
      show_materials: template.show_materials !== false,
      primary_color: template.primary_color || '#0284c7'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    try {
      await api.delete('/report-templates/' + templateId);
      toast.success('Plantilla eliminada');
      loadTemplates();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await api.patch('/report-templates/' + templateId + '/set-default');
      toast.success('Plantilla establecida como predeterminada');
      loadTemplates();
    } catch (err) {
      toast.error('Error al establecer predeterminada');
    }
  };

  const addChecklistItem = () => {
    const trimmed = newChecklistItem.trim();
    if (trimmed) {
      const updated = [...formData.checklist_items, trimmed];
      setFormData({ ...formData, checklist_items: updated });
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index) => {
    const updated = formData.checklist_items.filter((_, i) => i !== index);
    setFormData({ ...formData, checklist_items: updated });
  };

  const addParameter = () => {
    const trimmedName = newParamName.trim();
    if (trimmedName) {
      const newParam = { name: trimmedName, unit: newParamUnit.trim() };
      const updated = [...formData.parameters, newParam];
      setFormData({ ...formData, parameters: updated });
      setNewParamName('');
      setNewParamUnit('');
    }
  };

  const removeParameter = (index) => {
    const updated = formData.parameters.filter((_, i) => i !== index);
    setFormData({ ...formData, parameters: updated });
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  const dialogTitle = editingTemplate ? 'Editar plantilla' : 'Nueva plantilla';
  const submitText = submitting ? 'Guardando...' : (editingTemplate ? 'Guardar cambios' : 'Crear plantilla');

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Plantillas de Parte</h1>
            <p className="text-slate-500">Personaliza los formularios para los trabajadores</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva plantilla
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{dialogTitle}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre de la plantilla *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Mantenimiento estándar"
                      required
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Color principal</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={formData.primary_color}
                          onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={formData.primary_color}
                          onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                          placeholder="#0284c7"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Opciones de visualización</Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={formData.show_photos} onChange={(e) => setFormData({ ...formData, show_photos: e.target.checked })} className="rounded" />
                          Mostrar fotos
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={formData.show_parameters} onChange={(e) => setFormData({ ...formData, show_parameters: e.target.checked })} className="rounded" />
                          Mostrar parámetros
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={formData.show_materials} onChange={(e) => setFormData({ ...formData, show_materials: e.target.checked })} className="rounded" />
                          Mostrar materiales
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>Checklist</Label>
                  <div className="flex gap-2">
                    <Input value={newChecklistItem} onChange={(e) => setNewChecklistItem(e.target.value)} placeholder="Nueva tarea..." onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }} />
                    <Button type="button" variant="outline" onClick={addChecklistItem}><Plus className="w-4 h-4" /></Button>
                  </div>
                  <ChecklistList items={formData.checklist_items} onRemove={removeChecklistItem} />
                </div>
                <div className="space-y-3">
                  <Label>Parámetros a medir</Label>
                  <div className="flex gap-2">
                    <Input value={newParamName} onChange={(e) => setNewParamName(e.target.value)} placeholder="Nombre (ej: pH)" className="flex-1" />
                    <Input value={newParamUnit} onChange={(e) => setNewParamUnit(e.target.value)} placeholder="Unidad" className="w-24" />
                    <Button type="button" variant="outline" onClick={addParameter}><Plus className="w-4 h-4" /></Button>
                  </div>
                  <ParameterList items={formData.parameters} onRemove={removeParameter} />
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Texto de cabecera (opcional)</Label>
                    <Textarea value={formData.header_text} onChange={(e) => setFormData({ ...formData, header_text: e.target.value })} placeholder="Texto adicional en la cabecera del PDF..." rows={2} />
                  </div>
                  <div className="space-y-2">
                    <Label>Texto de pie (opcional)</Label>
                    <Textarea value={formData.footer_text} onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })} placeholder="Texto al final del PDF..." rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={submitting} data-testid="save-template-btn">{submitText}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <TemplateList templates={templates} onEdit={handleEdit} onDelete={handleDelete} onSetDefault={handleSetDefault} />
      </div>
    </OwnerLayout>
  );
};

export default Templates;
